import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    me: () => api.get('/auth/me'),
    changePassword: (userId, newPassword) => api.put('/auth/change-password', { user_id: userId, new_password: newPassword }),
    changeOwnPassword: (currentPassword, newPassword) => api.put('/auth/change-own-password', { current_password: currentPassword, new_password: newPassword }),
    getUsuarios: () => api.get('/usuarios'),
    createUsuario: (userData) => api.post('/usuarios', userData),
    updateUsuario: (userId, data) => api.put(`/usuarios/${userId}`, data),
    deleteUsuario: (userId) => api.delete(`/usuarios/${userId}`),
};

// Empresas
export const empresaAPI = {
    getAll: () => api.get('/empresas'),
    getById: (id) => api.get(`/empresas/${id}`),
    getBySubdominio: (subdominio) => api.get(`/empresas/subdominio/${subdominio}`),
    create: (data) => api.post('/empresas', data),
    updateConfig: (id, data) => api.put(`/empresas/${id}/configuracion`, data),
};

// Documentos
export const documentoAPI = {
    getAll: (estado = null) => api.get('/documentos', { params: { estado } }),
    getAllAdmin: () => api.get('/documentos/todos'),
    getById: (id) => api.get(`/documentos/${id}`),
    create: (data) => api.post('/documentos', data),
    aprobar: (id, data) => api.post(`/documentos/${id}/aprobar`, data),
    despachar: (id, data) => api.post(`/documentos/${id}/despachar`, data),
    uploadAdjunto: (formData) => api.post('/documentos/upload-adjunto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadFirmaAprobador: (id, formData) => api.post(`/documentos/${id}/upload-firma-aprobador`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Configuración de Guías de Despacho
export const guiasConfigAPI = {
    getConfiguracion: () => api.get('/guias/configuracion'),
    updateConfiguracion: (data) => api.put('/guias/configuracion', data),
    consultarRut: (rut) => api.get(`/guias/consultar-rut/${encodeURIComponent(rut)}`),
    normalizarRut: (rut) => api.post('/guias/normalizar-rut', { rut }),
    getAprobadores: () => api.get('/guias/aprobadores'),
};

// Usuarios API
export const usuariosAPI = {
    getAll: () => api.get('/usuarios'),
    getPlantillaExcel: () => api.get('/usuarios/plantilla-excel', { responseType: 'blob' }),
    cargaMasiva: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/usuarios/carga-masiva', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

// Master tables
export const vehiculoAPI = {
    getAll: (empresaId = null) => api.get('/vehiculos', { params: { empresa_id: empresaId } }),
    buscar: (patente) => api.get(`/vehiculos/buscar/${patente}`),
    create: (data) => api.post('/vehiculos', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/vehiculos', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

export const entidadAPI = {
    getAll: (empresaId = null) => api.get('/entidades', { params: { empresa_id: empresaId } }),
    buscar: (rut) => api.get(`/entidades/buscar/${rut}`),
    create: (data) => api.post('/entidades', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/entidades', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

export const personaAPI = {
    getAll: (empresaId = null) => api.get('/personas', { params: { empresa_id: empresaId } }),
    create: (data) => api.post('/personas', data),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/personas', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

// Notifications
export const notificacionAPI = {
    getAll: () => api.get('/notificaciones'),
    markRead: (id) => api.put(`/notificaciones/${id}/leer`),
    markAllRead: () => api.put('/notificaciones/leer-todas'),
};

// History
export const historialAPI = {
    getAll: (documentoId = null) => api.get('/historial', { params: { documento_id: documentoId } }),
};

// Users
export const usuarioAPI = {
    getAll: () => api.get('/usuarios'),
    update: (id, data) => api.put(`/usuarios/${id}`, data),
    delete: (id) => api.delete(`/usuarios/${id}`),
};

// Stats
export const statsAPI = {
    get: () => api.get('/stats'),
};

// Modulos
export const moduloAPI = {
    getAll: () => api.get('/modulos'),
    getEmpresa: () => api.get('/modulos/empresa'),
    create: (data) => api.post('/modulos', data),
    asignar: (empresaId, modulos) => api.post(`/empresas/${empresaId}/modulos`, { empresa_id: empresaId, modulos }),
};

// SuperAdmin
export const superadminAPI = {
    getEmpresas: () => api.get('/superadmin/empresas'),
    createEmpresa: (data) => api.post('/superadmin/empresas', data),
    getUsuarios: () => api.get('/superadmin/usuarios'),
    createUsuario: (data) => api.post('/superadmin/usuarios', data),
    deleteUsuario: (userId) => api.delete(`/usuarios/${userId}`),
};

// Seed data
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// ==================== BOMBEROS MODULE ====================

// Compañías de Bomberos
export const bomberosCompaniaAPI = {
    getAll: () => api.get('/bomberos/companias'),
    getById: (id) => api.get(`/bomberos/companias/${id}`),
    create: (data) => api.post('/bomberos/companias', data),
    update: (id, data) => api.put(`/bomberos/companias/${id}`, data),
    delete: (id) => api.delete(`/bomberos/companias/${id}`),
};

// Competencias
export const bomberosCompetenciaAPI = {
    getAll: () => api.get('/bomberos/competencias'),
    create: (data) => api.post('/bomberos/competencias', data),
};

// Personal (Bomberos)
export const bomberosPersonalAPI = {
    getAll: (params = {}) => api.get('/bomberos/personal', { params }),
    getById: (id) => api.get(`/bomberos/personal/${id}`),
    getCapacitaciones: (id) => api.get(`/bomberos/personal/${id}/capacitaciones`),
    create: (data) => api.post('/bomberos/personal', data),
    update: (id, data) => api.put(`/bomberos/personal/${id}`, data),
    delete: (id, permanente = false) => api.delete(`/bomberos/personal/${id}?permanente=${permanente}`),
    activar: (id) => api.post(`/bomberos/personal/${id}/activar`),
    asignarMallas: () => api.post('/bomberos/personal/asignar-mallas'),
    asignarMalla: (id, malla) => api.put(`/bomberos/personal/${id}/malla`, { malla_asignada: malla }),
};

// Importación Masiva
export const bomberosImportacionAPI = {
    importar: (tipo, registros, companiaId) => api.post('/bomberos/importacion', { 
        tipo, 
        registros, 
        compania_id: companiaId 
    }),
    revertir: (ids, tipo = 'personal') => api.post('/bomberos/importacion/revertir', { ids, tipo }),
};

// Capacitaciones
export const bomberosCapacitacionAPI = {
    getAll: (params = {}) => api.get('/bomberos/capacitaciones', { params }),
    getById: (id) => api.get(`/bomberos/capacitaciones/${id}`),
    create: (data) => api.post('/bomberos/capacitaciones', data),
    update: (id, data) => api.put(`/bomberos/capacitaciones/${id}`, data),
    delete: (id) => api.delete(`/bomberos/capacitaciones/${id}`),
    validar: (id, data) => api.post(`/bomberos/capacitaciones/${id}/validar`, data),
    importar: (registros) => api.post('/bomberos/capacitaciones/importar', { registros }),
    asignarMalla: (data) => api.put('/bomberos/capacitaciones/asignar-malla', data),
    getMallaCurricular: (anio) => api.get(`/bomberos/malla-curricular?anio=${anio}`),
};

// Malla Curricular (cursos fijos)
export const bomberosMallaAPI = {
    getCursos: (anio) => api.get(`/bomberos/malla-curricular?anio=${anio}`),
    getCursosLista: () => api.get('/bomberos/malla/cursos-lista'),
    seed: (anio, cursos) => api.post('/bomberos/malla/seed', { anio, cursos }),
    cargarOficial: () => api.post('/bomberos/malla/cargar-oficial'),
    getProgreso: (bomberoId, anio) => api.get(`/bomberos/malla/progreso/${bomberoId}?anio=${anio}`),
    updateProgreso: (data) => api.put('/bomberos/malla/progreso', data),
    updateCurso: (cursoId, data) => api.put(`/bomberos/malla/curso/${cursoId}`, data),
    reordenarCursos: (cursos) => api.put('/bomberos/malla/cursos/reordenar', { cursos }),
    createCurso: (data) => api.post('/bomberos/malla/curso', data),
    deleteCurso: (cursoId) => api.delete(`/bomberos/malla/curso/${cursoId}`),
    getBomberosByCurso: (cursoId, anio) => api.get(`/bomberos/malla/curso/${cursoId}/bomberos?anio=${anio}`),
    getInformeGlobal: (anio, { fecha_inicio, fecha_fin } = {}) => {
        let url = `/bomberos/malla/informe-global?anio=${anio}`;
        if (fecha_inicio) url += `&fecha_inicio=${fecha_inicio}`;
        if (fecha_fin) url += `&fecha_fin=${fecha_fin}`;
        return api.get(url);
    },
};

// Actividades Extra Programáticas
export const bomberosActividadesAPI = {
    getAll: (params = {}) => api.get('/bomberos/actividades', { params }),
    getById: (id) => api.get(`/bomberos/actividades/${id}`),
    create: (data) => api.post('/bomberos/actividades', data),
    update: (id, data) => api.put(`/bomberos/actividades/${id}`, data),
    delete: (id) => api.delete(`/bomberos/actividades/${id}`),
    validar: (id, data) => api.post(`/bomberos/actividades/${id}/validar`, data),
};

// Asistencias
export const bomberosAsistenciaAPI = {
    getByCapacitacion: (capacitacionId) => api.get(`/bomberos/asistencias/${capacitacionId}`),
    registrar: (data) => api.post('/bomberos/asistencias', data),
    uploadDocumento: (file, capacitacionId, fecha) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/bomberos/asistencias/upload-documento?capacitacion_id=${capacitacionId}&fecha=${fecha}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    uploadDocumentos: async (files, capacitacionId, fecha) => {
        const urls = [];
        for (const file of files) {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post(`/bomberos/asistencias/upload-documento?capacitacion_id=${capacitacionId}&fecha=${fecha}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            urls.push(res.data.url);
        }
        return urls;
    },
    getDocumentos: (capacitacionId, fecha) => api.get(`/bomberos/asistencias/documentos/${capacitacionId}/${fecha}`),
};

// Evaluaciones
export const bomberosEvaluacionAPI = {
    getByCapacitacion: (capacitacionId) => api.get(`/bomberos/evaluaciones/${capacitacionId}`),
    registrar: (data) => api.post('/bomberos/evaluaciones', data),
};

// Certificados
export const bomberosCertificadoAPI = {
    getDisponibles: (capacitacionId) => api.get(`/bomberos/certificados-disponibles/${capacitacionId}`),
    descargar: (capacitacionId, bomberoId) => 
        `${api.defaults.baseURL}/bomberos/certificado/${capacitacionId}/${bomberoId}`,
};

// Material Mayor
export const bomberosMaterialMayorAPI = {
    getAll: (params = {}) => api.get('/bomberos/material-mayor', { params }),
    getById: (id) => api.get(`/bomberos/material-mayor/${id}`),
    create: (data) => api.post('/bomberos/material-mayor', data),
    update: (id, data) => api.put(`/bomberos/material-mayor/${id}`, data),
    delete: (id) => api.delete(`/bomberos/material-mayor/${id}`),
};

// Checklists
export const bomberosChecklistAPI = {
    getByMaterial: (materialId) => api.get(`/bomberos/checklists/${materialId}`),
    create: (data) => api.post('/bomberos/checklists', data),
};

// Catálogo de Items para Vehículos
export const bomberosItemsCatalogoAPI = {
    getAll: (params = {}) => api.get('/bomberos/items-catalogo', { params }),
    getCategorias: () => api.get('/bomberos/items-catalogo/categorias'),
    create: (data) => api.post('/bomberos/items-catalogo', data),
    update: (id, data) => api.put(`/bomberos/items-catalogo/${id}`, data),
    delete: (id) => api.delete(`/bomberos/items-catalogo/${id}`),
};

// Inventario de Vehículos
export const bomberosInventarioAPI = {
    getByVehiculo: (vehiculoId) => api.get(`/bomberos/material-mayor/${vehiculoId}/inventario`),
    addItem: (vehiculoId, data) => api.post(`/bomberos/material-mayor/${vehiculoId}/inventario`, data),
    updateItem: (inventarioId, data) => api.put(`/bomberos/inventario/${inventarioId}`, data),
    deleteItem: (inventarioId) => api.delete(`/bomberos/inventario/${inventarioId}`),
};

// Material Menor / EPP
export const bomberosMaterialMenorAPI = {
    getAll: (params = {}) => api.get('/bomberos/material-menor', { params }),
    getVencimientos: () => api.get('/bomberos/epp/vencimientos'),
    create: (data) => api.post('/bomberos/material-menor', data),
    update: (id, data) => api.put(`/bomberos/material-menor/${id}`, data),
    delete: (id) => api.delete(`/bomberos/material-menor/${id}`),
    asignar: (materialId, data) => api.post(`/bomberos/material-menor/${materialId}/asignar`, data),
    desasignar: (materialId) => api.post(`/bomberos/material-menor/${materialId}/desasignar`),
};

// Solicitudes de Baja de EPP
export const bomberosSolicitudesBajaAPI = {
    getAll: (estado = null) => api.get('/bomberos/solicitudes-baja-epp', { params: { estado } }),
    create: (data) => api.post('/bomberos/solicitudes-baja-epp', data),
    validar: (id, data) => api.post(`/bomberos/solicitudes-baja-epp/${id}/validar`, data),
    uploadEvidencia: (formData) => api.post('/bomberos/solicitudes-baja-epp/upload-evidencia', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Asignación EPP
export const bomberosAsignacionEPPAPI = {
    asignar: (data) => api.post('/bomberos/asignacion-epp', data),
    devolver: (materialId, data) => api.post(`/bomberos/devolucion-epp/${materialId}`, data),
};

// Actas de Actividad
export const bomberosActaAPI = {
    getAll: (params = {}) => api.get('/bomberos/actas', { params }),
    getById: (id) => api.get(`/bomberos/actas/${id}`),
    create: (data) => api.post('/bomberos/actas', data),
};

// KPIs
export const bomberosKPIAPI = {
    get: (params = {}) => api.get('/bomberos/kpis', { params }),
};

// Dashboard Analytics
export const bomberosDashboardAPI = {
    getAnalytics: () => api.get('/bomberos/dashboard/analytics'),
};

// Estadísticas Comparativas
export const bomberosEstadisticasAPI = {
    getComparativas: (periodo = 'anual') => api.get('/bomberos/estadisticas/comparativas', { params: { periodo } }),
    getTendencias: (meses = 6) => api.get('/bomberos/estadisticas/tendencias', { params: { meses } }),
    getResumen: () => api.get('/bomberos/estadisticas/resumen'),
    getDetallado: () => api.get('/bomberos/estadisticas/detallado'),
};

// Alertas
export const bomberosAlertaAPI = {
    getAll: (params = {}) => api.get('/bomberos/alertas', { params }),
    resolver: (id) => api.put(`/bomberos/alertas/${id}/resolver`),
    generar: () => api.post('/bomberos/alertas/generar'),
};

// Seed Bomberos
export const bomberosSeedAPI = {
    seed: () => api.post('/bomberos/seed'),
};

// Roles y Permisos Bomberos
export const bomberosRolesAPI = {
    getAll: () => api.get('/bomberos/roles'),
    getById: (id) => api.get(`/bomberos/roles/${id}`),
    create: (data) => api.post('/bomberos/roles', data),
    update: (id, data) => api.put(`/bomberos/roles/${id}`, data),
    delete: (id) => api.delete(`/bomberos/roles/${id}`),
    inicializar: () => api.post('/bomberos/roles/inicializar'),
    getPermisosDisponibles: () => api.get('/bomberos/permisos-disponibles'),
    getMisPermisos: () => api.get('/bomberos/mis-permisos'),
};

// Usuarios con Roles de Bomberos
export const bomberosUsuariosRolesAPI = {
    getAll: () => api.get('/bomberos/usuarios-roles'),
    asignar: (data) => api.post('/bomberos/usuarios-roles', data),
    remover: (usuarioId) => api.delete(`/bomberos/usuarios-roles/${usuarioId}`),
};

// Registro de Actividades (Auditoría)
export const registroActividadesAPI = {
    getAll: (params = {}) => api.get('/registro-actividades', { params }),
    revertir: (id) => api.post(`/registro-actividades/${id}/revertir`),
    eliminar: (id) => api.delete(`/registro-actividades/${id}`),
    limpiar: (fechaHasta = null) => api.delete('/registro-actividades', { params: { fecha_hasta: fechaHasta } }),
};

export default api;
